import { useState } from "react";
import { NavLink, useLocation } from "react-router-dom";
import {
  AcademicCapIcon,
  PhotoIcon,
  ArrowTrendingDownIcon,
  HomeIcon,
  BuildingOffice2Icon,
  UsersIcon,
  UserGroupIcon,
  DocumentDuplicateIcon,
  DocumentTextIcon,
  EnvelopeIcon,
  ChartBarIcon,
  CpuChipIcon,
  CurrencyDollarIcon,
  Cog6ToothIcon,
  UserCircleIcon,
  ShieldCheckIcon,
  BellAlertIcon,
  ClipboardDocumentListIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  ChevronDownIcon,
  KeyIcon,
  FunnelIcon,
  MegaphoneIcon,
  BuildingStorefrontIcon,
  ShieldExclamationIcon,
  DocumentChartBarIcon,
  CalendarDaysIcon,
  ChartBarSquareIcon,
  BanknotesIcon,
  CommandLineIcon,
  SignalIcon,
  MagnifyingGlassIcon,
  MapIcon,
  MapPinIcon,
  IdentificationIcon,
  GlobeAltIcon,
  WrenchScrewdriverIcon,
  SparklesIcon,
  NewspaperIcon,
  PaperAirplaneIcon,
  ChatBubbleBottomCenterTextIcon,
  MusicalNoteIcon,
  RectangleStackIcon,
  FilmIcon,
  CloudArrowDownIcon,
  ShareIcon,
} from "@heroicons/react/24/outline";
import { PermissionGate } from "@/components/shared/PermissionGate";
import { PERMISSIONS, type Permission } from "@/utils/permissions";
import {
  useIsDataAdmin,
  useIsOwner,
  useIsSuperAdmin,
} from "@/components/shared/RequireSuperAdmin";
import { useTenantDirectory } from "@/api/tenants";
import { useBrandingStore } from "@/store/brandingStore";
import { brandingAssetUrl } from "@/api/branding";

interface AdminSidebarProps {
  collapsed: boolean;
  onToggle: () => void;
  mobileOpen?: boolean;
  onNavigate?: () => void;
}

interface NavItem {
  to: string;
  label: string;
  icon: typeof HomeIcon;
  permission: Permission;
  /** Hide from anyone whose role is not SUPER_ADMIN. The route itself is
   *  wrapped in <RequireSuperAdmin> and the API enforces it server-side —
   *  this only keeps the link out of the menu. */
  superAdminOnly?: boolean;
  /** DATA_ADMIN or SUPER_ADMIN — mirrors the API guard (MPPI-00711). */
  dataAdminOnly?: boolean;
  /** Hide from anyone whose scope is not `platform` (MPPI-00313).
   *
   *  Menu visibility only — `require_platform_admin` refuses these endpoints
   *  server-side regardless. The point of hiding is not the permission, it is
   *  not offering a tenant admin a door that will not open. */
  platformOnly?: boolean;
  /** Shown only when this per-tenant feature is switched on (MPPI-00642).
   *  Platform staff hold every feature, so they always see it. Menu
   *  visibility only — the route guard is what stops the chunk loading. */
  feature?: string;
}

interface NavSection {
  kind: "section";
  label: string;
  icon: typeof HomeIcon;
  items: NavItem[];
  /** A short flag beside the section name, e.g. "NEW!". Cosmetic only; it
   *  carries no permission meaning and never gates anything. */
  badge?: string;
  /** Visible only to the platform owner account. Menu visibility only — the
   *  endpoints behind these sections are gated server-side by role. */
  ownerOnly?: boolean;
}

interface NavStandalone extends NavItem {
  kind: "standalone";
}

type NavEntry = NavSection | NavStandalone;

const navigation: NavEntry[] = [
  {
    kind: "standalone",
    to: "/",
    label: "Dashboard",
    icon: HomeIcon,
    permission: PERMISSIONS.DASHBOARD_VIEW,
  },
  {
    kind: "section",
    // MPPI-00927. The video tooling had grown to six pages scattered through
    // a section of unrelated operator tools, which is how the operator came
    // to ask where the Clip Library was. Gathered here, directly under
    // Dashboard, because this is now the most-used part of the admin.
    //
    // MOVED, not copied: the same page listed under two sections makes the
    // menu imply two subjects (the lesson of MPPI-00091, one entry per page).
    label: "Video Tools",
    icon: FilmIcon,
    badge: "NEW!",
    items: [
      // Per-tenant switch rather than platform-only (MPPI-00642): a tenant's
      // own admins make their own branded videos once it is enabled.
      { to: "/tools/ad-hoc-video", label: "Ad-hoc Video", icon: SparklesIcon, permission: PERMISSIONS.DASHBOARD_VIEW, feature: "adhoc_videos" },
      { to: "/tools/generated-videos", label: "Generated Videos", icon: FilmIcon, permission: PERMISSIONS.DASHBOARD_VIEW },
      { to: "/tools/video-download", label: "Video Download", icon: CloudArrowDownIcon, permission: PERMISSIONS.DASHBOARD_VIEW },
      { to: "/tools/lyric-video", label: "Lyric Video", icon: MusicalNoteIcon, permission: PERMISSIONS.DASHBOARD_VIEW },
      { to: "/tools/video-summary", label: "Video Summary", icon: ChatBubbleBottomCenterTextIcon, permission: PERMISSIONS.DASHBOARD_VIEW },
      { to: "/tools/clip-library", label: "Clip Library", icon: RectangleStackIcon, permission: PERMISSIONS.DASHBOARD_VIEW },
    ],
  },
  {
    kind: "section",
    // MPPI-00091. Items inside the "Off-Plan" section used to repeat
    // "Off-Plan · " in every label, so the section name was read twice on the
    // way to every page — the operator called it a wasted click.
    label: "Off-Plan",
    icon: BuildingStorefrontIcon,
    items: [
      // One entry (MPPI-00114). The project records table is reachable from a
      // tab on the page itself rather than a second menu line — they are two
      // views of the same 1,947 off-plan projects, and splitting them made the
      // menu imply two subjects.
      // Split out of the in-page tab strip (MPPI-00150) so each view is a
      // real, linkable page.
      //
      // "Project Listings" (/projects) was removed from this menu on
      // 2026-09-18 (MPPI-00656): it was a second line pointing at the same
      // 1,947 off-plan projects, which is what the note above already warned
      // splitting them would imply. "Projects" now offers both a panel and a
      // list view, so the table is a toggle rather than a menu item. The
      // /projects ROUTE still exists — it carries edit permissions the rest
      // of Off-Plan does not, and old links to it keep working.
      { to: "/market/offplan", label: "Projects", icon: BuildingStorefrontIcon, permission: PERMISSIONS.PROPERTIES_VIEW },
      // MPPI-00719. Where each project has REACHED, as opposed to what it is.
      { to: "/market/offplan/lifecycle", label: "Project Lifecycle", icon: BuildingOffice2Icon, permission: PERMISSIONS.PROPERTIES_VIEW },
      { to: "/market/offplan/map", label: "Map", icon: MapIcon, permission: PERMISSIONS.PROPERTIES_VIEW },
      { to: "/market/offplan/pulse", label: "Market Pulse", icon: ChartBarIcon, permission: PERMISSIONS.PROPERTIES_VIEW },
      // MPPI-00166. The authority's own boundaries and registered developers
      // — the only project data here nobody inferred.
      { to: "/market/approved-projects", label: "Approved Developments", icon: MapIcon, permission: PERMISSIONS.PROPERTIES_VIEW },
    ],
  },
  {
    kind: "section",
    label: "Ready to Move",
    icon: GlobeAltIcon,
    items: [
      // Map first — it is the landing view for this channel (MPPI-00182).
      // The section links to its first item, so ordering IS the default.
      { to: "/market/secondary", label: "Ready Market Map", icon: GlobeAltIcon, permission: PERMISSIONS.PROPERTIES_VIEW },
      { to: "/properties", label: "For Sale Properties", icon: BuildingOffice2Icon, permission: PERMISSIONS.PROPERTIES_VIEW },
      // "Secondary Market" understated this page by ~19%: the endpoint has
      // always returned developer-primary stock alongside owner resale.
      // "Ready" is what it shows; the channel tabs split it (MPPI-00091).
      // Buy-side despite the name (MPPI-00099) — its own docstring says it
      // surfaces "buy this, rent it out" candidates.
      { to: "/yield-arbitrage", label: "Rent-vs-Buy Arbitrage", icon: ChartBarIcon, permission: PERMISSIONS.PROPERTIES_VIEW, dataAdminOnly: true },
      { to: "/buildings", label: "Buildings", icon: BuildingOffice2Icon, permission: PERMISSIONS.PROPERTIES_VIEW },
      // MPPI-00080. Sits in Ready to Move because it filters active sale
      // listings — the seller signals only exist where there is a live asking
      // price to have been cut.
      { to: "/market/distressed", label: "Distressed Deals", icon: ArrowTrendingDownIcon, permission: PERMISSIONS.PROPERTIES_VIEW },
      { to: "/market/best-deals", label: "Best Deals", icon: SparklesIcon, permission: PERMISSIONS.PROPERTIES_VIEW },
      { to: "/market/motivated-sellers", label: "Price Change Diagnostics", icon: ArrowTrendingDownIcon, permission: PERMISSIONS.PROPERTIES_VIEW },
    ],
  },
  {
    kind: "section",
    label: "Rental",
    icon: KeyIcon,
    items: [
      // Map first, matching Ready to Move (MPPI-00182).
      { to: "/market/rental-map", label: "Rental Market Map", icon: MapIcon, permission: PERMISSIONS.PROPERTIES_VIEW },
      { to: "/rental-properties", label: "Rental Properties", icon: KeyIcon, permission: PERMISSIONS.PROPERTIES_VIEW },
      { to: "/rental-market", label: "Rental Market", icon: ChartBarIcon, permission: PERMISSIONS.PROPERTIES_VIEW },
      // MPPI-00153. The sale side has had a map since the platform was built;
      // rental had none, despite being the larger corpus and the only one
      // that is 100% mappable — every active rental has coordinates and a rent.
    ],
  },
  {
    kind: "section",
    label: "Communities",
    icon: MapIcon,
    items: [
      { to: "/market/communities", label: "Community Insights", icon: MapIcon, permission: PERMISSIONS.PROPERTIES_VIEW },
      // Market Intelligence keeps a home here rather than being deleted. It is
      // the DLD transaction view, and community is the lens it is read
      // through. MPPI-00091 dissolves it into per-channel panels; until those
      // exist, dropping the link would remove the only route to transaction
      // data.
      { to: "/market", label: "Transactions Map", icon: ChartBarIcon, permission: PERMISSIONS.PROPERTIES_VIEW },
    ],
  },
  {
    kind: "section",
    label: "Dubai Today",
    icon: GlobeAltIcon,
    items: [
      { to: "/dubai-pulse", label: "City Pulse", icon: GlobeAltIcon, permission: PERMISSIONS.DASHBOARD_VIEW },
      { to: "/tools/dubai-daily-briefing", label: "Daily Briefing", icon: NewspaperIcon, permission: PERMISSIONS.DASHBOARD_VIEW },
    ],
  },
  {
    kind: "section",
    // Named "Data & Sources", NOT "Data": a separate owner-only section
    // already used that label for data QUALITY and operations. Two menus
    // called Data would recreate exactly the ambiguity this restructure is
    // removing, so the other one is now "Data Quality".
    label: "Data & Sources",
    icon: BuildingOffice2Icon,
    items: [
      { to: "/transactions", label: "DLD Transactions", icon: BanknotesIcon, permission: PERMISSIONS.PROPERTIES_VIEW },
      { to: "/ejari", label: "DLD Rents (Ejari)", icon: BanknotesIcon, permission: PERMISSIONS.PROPERTIES_VIEW },
      { to: "/listing-agents", label: "Selling Agents", icon: IdentificationIcon, permission: PERMISSIONS.PROPERTIES_VIEW },
      { to: "/property-lookup", label: "Title Lookup & Owners", icon: MagnifyingGlassIcon, permission: PERMISSIONS.PROPERTY_LOOKUP_VIEW, dataAdminOnly: true },
    ],
  },
  {
    kind: "section",
    label: "Leads",
    icon: FunnelIcon,
    items: [
      { to: "/leads/pipeline", label: "Lead Pipeline", icon: FunnelIcon, permission: PERMISSIONS.LEADS_VIEW },
      { to: "/leads/prospects", label: "Prospects", icon: UsersIcon, permission: PERMISSIONS.LEADS_VIEW },
      { to: "/leads/outreach", label: "Outreach", icon: MegaphoneIcon, permission: PERMISSIONS.LEADS_VIEW, dataAdminOnly: true },
      { to: "/leads/reports", label: "Lead Reports", icon: DocumentChartBarIcon, permission: PERMISSIONS.LEADS_VIEW, dataAdminOnly: true },
      { to: "/leads/scoring", label: "Lead Scoring", icon: ChartBarSquareIcon, permission: PERMISSIONS.LEADS_VIEW, dataAdminOnly: true },
      { to: "/leads/visits", label: "Visits", icon: CalendarDaysIcon, permission: PERMISSIONS.LEADS_VIEW },
    ],
  },
  // Vehicles hidden 2026-08-19 (MPPI-00154). The platform is a property
  // intelligence product and the vehicle vertical is not being pursued; the
  // routes and pages remain in the codebase, unreachable, so the decision is
  // reversible by restoring this block rather than rebuilding the feature.
  {
    kind: "section",
    // Renamed from "Users" (MPPI-00314). Everyone in here now belongs to a
    // tenant: agents work for one, investors are its clients, billing is its
    // account. "Users" described a flat list of people; this describes the
    // thing they hang off.
    // "Platform Tenants", not "Tenants" (MPPI-00503). In a property product
    // a tenant is someone renting a unit; here it is a white-label customer of
    // the platform. The bare word invited exactly the wrong reading.
    label: "Platform Tenants",
    ownerOnly: true,
    icon: UsersIcon,
    items: [
      { to: "/tenants", label: "Platform Tenants", icon: SparklesIcon, permission: PERMISSIONS.ROLES_MANAGE, platformOnly: true },
      { to: "/platform-users", label: "Investors", icon: UserCircleIcon, permission: PERMISSIONS.PLATFORM_USERS_VIEW },
      // Partner Users (Network) folded in here. `leads.partners` and
      // `leads.partner_users` held ZERO rows, so nothing was migrated and
      // nothing was lost — a partner is an agent who works for a tenant, and
      // maintaining two lists of tenant people was the split being removed.
      { to: "/agents", label: "Agents", icon: UserGroupIcon, permission: PERMISSIONS.AGENTS_VIEW },
      { to: "/tenant-activity", label: "Tenant Activity", icon: CurrencyDollarIcon, permission: PERMISSIONS.BILLING_VIEW, platformOnly: true },
      { to: "/billing", label: "Billing (legacy)", icon: CurrencyDollarIcon, permission: PERMISSIONS.BILLING_VIEW, platformOnly: true },
    ],
  },
  {
    kind: "section",
    label: "Tools",
    icon: WrenchScrewdriverIcon,
    items: [
      // Sidebar visibility uses DASHBOARD_VIEW so all admin roles see
      // the link; the backend role guard (SUPER_ADMIN/DATA_ADMIN/ANALYST)
      // is the actual access control.
      { to: "/market/sky", label: "UAE Live Sky", icon: PaperAirplaneIcon, permission: PERMISSIONS.DASHBOARD_VIEW },
      // Moved here from Communities (MPPI-00152). It is an operator tool —
      // a licensing review queue someone works through — rather than a
      // market surface, and it sits beside the other photo tooling.
      { to: "/market/photo-clearance", label: "Photo Clearance", icon: PhotoIcon, permission: PERMISSIONS.PROPERTIES_VIEW, platformOnly: true },
      { to: "/tools/photo-enhancer", label: "Photo Enhancer", icon: SparklesIcon, permission: PERMISSIONS.DASHBOARD_VIEW },
      { to: "/tools/automated-accounts", label: "Automated Accounts", icon: ShareIcon, permission: PERMISSIONS.DASHBOARD_VIEW, platformOnly: true },
    ],
  },
  {
    kind: "section",
    label: "Data Quality",
    ownerOnly: true,
    icon: ChartBarIcon,
    items: [
      { to: "/data-quality", label: "Data Quality", icon: ChartBarIcon, permission: PERMISSIONS.DATA_QUALITY_VIEW, platformOnly: true },
      { to: "/data/corrected-locations", label: "Corrected Locations", icon: MapPinIcon, permission: PERMISSIONS.PROPERTIES_EDIT, platformOnly: true },
      { to: "/data/duplicates", label: "Possible Duplicates", icon: DocumentDuplicateIcon, permission: PERMISSIONS.PROPERTIES_EDIT, platformOnly: true },
      { to: "/leads/compliance", label: "Data Compliance", icon: ShieldExclamationIcon, permission: PERMISSIONS.LEADS_COMPLIANCE, dataAdminOnly: true },
      { to: "/models", label: "AI Models", icon: CpuChipIcon, permission: PERMISSIONS.MODELS_VIEW, platformOnly: true },
    ],
  },
  // MPPI-00161. Content and Education used to live inside "Data Quality",
  // which hid them twice over: they are not data quality, and that section is
  // ownerOnly — so every admin who is not admin@mppi.ae had no route to the
  // curriculum at all, including SUPER_ADMINs. The operator could not find
  // Education for exactly this reason.
  //
  // This section is gated on CONTENT_VIEW, the permission the pages already
  // required, rather than on an identity. compliance_officer holds it
  // explicitly and super_admin holds everything, so the people meant to
  // publish can now reach the pages they were already authorised for.
  {
    kind: "section",
    label: "Content",
    icon: DocumentTextIcon,
    items: [
      { to: "/content", label: "Articles & Pages", icon: DocumentTextIcon, permission: PERMISSIONS.CONTENT_VIEW, platformOnly: true },
      // The curriculum existed only as a markdown file and the written pages
      // only in the generic Content list, so there was no way to see progress
      // — or that most of the gap is gated on human review (MPPI-00155).
      { to: "/education", label: "Education", icon: AcademicCapIcon, permission: PERMISSIONS.CONTENT_VIEW, platformOnly: true },
      { to: "/newsletter", label: "Newsletter", icon: EnvelopeIcon, permission: PERMISSIONS.NEWSLETTER_VIEW, platformOnly: true },
    ],
  },
  {
    kind: "section",
    label: "Admin",
    ownerOnly: true,
    icon: Cog6ToothIcon,
    items: [
      { to: "/settings", label: "Settings", icon: Cog6ToothIcon, permission: PERMISSIONS.SETTINGS_VIEW, platformOnly: true },
      { to: "/users", label: "Admin Users", icon: ShieldCheckIcon, permission: PERMISSIONS.USERS_VIEW, superAdminOnly: true },
      { to: "/roles", label: "Roles", icon: KeyIcon, permission: PERMISSIONS.ROLES_MANAGE, platformOnly: true },
      { to: "/operations", label: "Operations Dashboard", icon: SignalIcon, permission: PERMISSIONS.SETTINGS_VIEW, platformOnly: true },
      { to: "/alerts", label: "Alerts", icon: BellAlertIcon, permission: PERMISSIONS.ALERTS_VIEW, platformOnly: true },
      { to: "/audit-log", label: "Audit Log", icon: ClipboardDocumentListIcon, permission: PERMISSIONS.AUDIT_VIEW, platformOnly: true },
      { to: "/platform-logs", label: "Platform Logs", icon: CommandLineIcon, permission: PERMISSIONS.LOGS_VIEW, platformOnly: true },
      { to: "/tier2-review", label: "Tier-2 LLM Review", icon: CpuChipIcon, permission: PERMISSIONS.PROPERTIES_VIEW, platformOnly: true },
      { to: "/scraper-live-feed", label: "Live Feeds", icon: SignalIcon, permission: PERMISSIONS.LOGS_VIEW, platformOnly: true },
    ],
  },
];

function getSectionForPath(pathname: string): string | null {
  for (const entry of navigation) {
    if (entry.kind !== "section") continue;
    if (
      entry.items.some(
        (item) =>
          pathname === item.to || pathname.startsWith(item.to + "/"),
      )
    ) {
      return entry.label;
    }
  }
  return null;
}

export function AdminSidebar({
  collapsed,
  onToggle,
  mobileOpen = false,
  onNavigate,
}: AdminSidebarProps) {
  const location = useLocation();
  const isSuperAdmin = useIsSuperAdmin();
  const isDataAdmin = useIsDataAdmin();
  const isOwner = useIsOwner();
  // Scope comes from the server (`/admin/tenants/directory`), never from the
  // signed-in email domain: a client-side guess that disagrees with
  // `resolve_scope` shows the wrong menu to exactly the people it matters for.
  const { isPlatform, hasFeature } = useTenantDirectory();
  const brand = useBrandingStore((s) => s.branding);
  const brandLogo = brandingAssetUrl(brand.logo_url) ?? "/icon.png";
  const [expandedSection, setExpandedSection] = useState<string | null>(
    () => getSectionForPath(location.pathname),
  );

  const toggleSection = (label: string) => {
    setExpandedSection((prev) => (prev === label ? null : label));
  };

  // On mobile the sidebar is a full-width off-canvas drawer (labels always
  // shown); on md+ it's the in-flow rail that can collapse to icons.
  //
  // The base class list must NOT also carry a width (MPPI-00316). It used to
  // contain a hardcoded `md:w-56` alongside this variable, and the two have
  // identical specificity — so the winner is whichever Tailwind emits LAST in
  // the stylesheet, not whichever appears last in the attribute. Tailwind
  // orders `w-16` before `w-56`, so the fixed class always won and the rail
  // kept its full width while the labels disappeared: collapsing hid the text
  // and reclaimed nothing.
  const width = collapsed ? "md:w-16" : "md:w-56";

  return (
    <aside
      className={`fixed inset-y-0 left-0 z-40 flex w-64 flex-col bg-primary transition-transform duration-200 md:static md:z-auto md:translate-x-0 md:transition-all ${width} ${
        mobileOpen ? "translate-x-0" : "-translate-x-full"
      }`}
    >
      {/* Logo */}
      <div className="flex h-20 items-center justify-between border-b border-white/10 px-4">
        {!collapsed ? (
          <div className="flex items-center gap-2">
            <img
              src={brandLogo}
              alt={brand.company_name}
              className="h-10 w-auto max-w-[190px] object-contain object-left"
            />
          </div>
        ) : (
          <img src={brandLogo} alt={brand.company_name} className="h-9 w-9 object-contain" />
        )}
        <button
          onClick={onToggle}
          className="rounded p-1 text-white/40 hover:bg-white/10 hover:text-white/70"
          aria-label={collapsed ? "Expand sidebar" : "Collapse sidebar"}
        >
          {collapsed ? (
            <ChevronRightIcon className="h-4 w-4" />
          ) : (
            <ChevronLeftIcon className="h-4 w-4" />
          )}
        </button>
      </div>

      {/* Navigation */}
      <nav className="flex-1 overflow-y-auto py-2">
        {navigation.map((entry) => {
          if (entry.kind === "standalone") {
            if (entry.superAdminOnly && !isSuperAdmin) return null;
            if (entry.dataAdminOnly && !isDataAdmin) return null;
            if (entry.platformOnly && !isPlatform) return null;
            if (entry.feature && !hasFeature(entry.feature)) return null;
            return (
              <PermissionGate key={entry.to} required={entry.permission}>
                <NavLink
                  to={entry.to}
                  end={entry.to === "/"}
                  onClick={onNavigate}
                  className={({ isActive }) =>
                    `flex items-center gap-3 px-4 py-2 text-sm transition-colors ${
                      isActive
                        ? "border-r-2 border-accent bg-white/10 font-medium text-accent"
                        : "text-white/70 hover:bg-white/5 hover:text-white"
                    } ${collapsed ? "justify-center" : ""}`
                  }
                  title={collapsed ? entry.label : undefined}
                >
                  <entry.icon className="h-5 w-5 flex-shrink-0" />
                  {!collapsed && <span>{entry.label}</span>}
                </NavLink>
              </PermissionGate>
            );
          }

          const section = entry;
          // Owner-only sections vanish entirely for everyone else.
          if (section.ownerOnly && !isOwner) return null;
          // A section whose every item is super-admin-only (e.g. Leads) would
          // otherwise render as an empty expander for other roles.
          const visibleItems = section.items.filter(
            (item) =>
              (!item.superAdminOnly || isSuperAdmin) &&
              (!item.dataAdminOnly || isDataAdmin) &&
              (!item.platformOnly || isPlatform) &&
              (!item.feature || hasFeature(item.feature)),
          );
          if (visibleItems.length === 0) return null;
          const isExpanded = expandedSection === section.label;
          const isSectionActive = section.items.some(
            (item) =>
              location.pathname === item.to ||
              location.pathname.startsWith(item.to + "/"),
          );

          return (
            <div key={section.label}>
              <button
                onClick={() => toggleSection(section.label)}
                className={`flex w-full items-center gap-3 px-4 py-2 text-sm transition-colors ${
                  isSectionActive && !isExpanded
                    ? "font-medium text-accent"
                    : "text-white/70 hover:bg-white/5 hover:text-white"
                } ${collapsed ? "justify-center" : ""}`}
                title={collapsed ? section.label : undefined}
              >
                <section.icon className="h-5 w-5 flex-shrink-0" />
                {!collapsed && (
                  <>
                    <span className="flex-1 text-left">{section.label}</span>
                    {section.badge && (
                      // A FIXED pair, not brand tokens (pattern 40). `navy`
                      // is not a colour in this config at all, so the first
                      // version emitted no text colour and left pale text on
                      // a gold pill; and `accent` is a tenant's own choice,
                      // which may be dark. Amber on near-black measures about
                      // 11:1 whatever the tenant has picked.
                      <span className="rounded bg-amber-400 px-1.5 py-0.5 text-[10px] font-bold leading-none text-neutral-900">
                        {section.badge}
                      </span>
                    )}
                    <ChevronDownIcon
                      className={`h-4 w-4 transition-transform ${
                        isExpanded ? "rotate-180" : ""
                      }`}
                    />
                  </>
                )}
              </button>

              {isExpanded && !collapsed && (
                <div className="pb-1">
                  {visibleItems.map((item) => (
                    <PermissionGate key={item.to} required={item.permission}>
                      <NavLink
                        to={item.to}
                        onClick={onNavigate}
                        className={({ isActive }) =>
                          `flex items-center gap-3 py-1.5 pl-10 pr-4 text-sm transition-colors ${
                            isActive
                              ? "border-r-2 border-accent bg-white/10 font-medium text-accent"
                              : "text-white/50 hover:bg-white/5 hover:text-white/80"
                          }`
                        }
                      >
                        <item.icon className="h-4 w-4 flex-shrink-0" />
                        <span>{item.label}</span>
                      </NavLink>
                    </PermissionGate>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </nav>
    </aside>
  );
}
